import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Dropdown, Modal, Select, Tag } from 'antd';

import RecordBadge from '../common/RecordBadge';
import GroupHeader from '../common/GroupHeader';
import { buildActionMenu } from '../common/actionMenu';
import { CHEVRON_DOWN, CHEVRON_UP, KEBAB, PLUS } from '../icons';
import { normalizeId } from '../../utils/ids';
import { formatCount } from '../../utils/counts';
import { elementSetPath } from '../../utils/elementSets';
import { confirmDestructive } from '../common/confirmDestructive';

/**
 * The card editor's ELEMENT SETS section (docs/plans/element-sets-and-me.md;
 * docs/platform-standards.md § The card page): the bundles this card
 * ATTACHES, in `elementSets[]` order, after its own element rows and before
 * the card-wide Primary date and Notes rows.
 *
 * A row is the set's badge, its element count and its scope; its ⋯ carries
 * Open set · Move up · Move down · Copy into this card · Remove. The sets an
 * ANCESTOR attaches are listed muted beneath, "from Person", with no menu —
 * a descendant inherits them and cannot shed them. "Add element set…" is a
 * select over what this card may attach: global sets, then its own
 * collection's, minus everything already carried.
 *
 * The list is part of the card's ONE draft: reordering, attaching and
 * removing all go through `onChange(ids)` and land with the card's Save.
 * "Copy into this card" is the exception — it is a server-side act on the
 * SAVED card (materialize), so it asks first and is offered only while the
 * draft is clean.
 *
 * Beneath the list, the PRIMARY DATE OFFER: when a set just attached
 * declares `options.suggestPrimaryDate` (To do: scheduled, then due) and
 * the card has no explicit primary date of its own, one closable line asks
 * "Use Scheduled, then Due as this card's primary date?" — Use it writes
 * the draft (`onUsePrimaryDate(tokens)`, saved with the same Save), Not now
 * puts it away. Never silent: the editor computes the offer, this renders it.
 */

/** A set-level refusal from the API, in the section's words. */
export function describeSetRefusal(error, { setsById = {} } = {}) {
	if (!error?.code) return null;
	const set = error.setId ? setsById[normalizeId(error.setId)] : null;
	const setName = set?.name || 'That element set';
	switch (error.code) {
		case 'element_set_conflict': {
			const other = error.conflictsWith;
			const otherName = other?.name ? `${other.kind === 'set' ? 'the element set' : 'the card'} "${other.name}"` : 'another definition';
			return `${setName} cannot be attached: its element "${error.element || ''}" is already defined by ${otherName}.`;
		}
		case 'element_set_redundant':
			return `${setName} is already attached by a parent card, so this card inherits it.`;
		case 'element_set_scope':
			return `${setName} belongs to another collection and cannot be attached here.`;
		case 'element_set_not_found':
			return `${setName} no longer exists. Remove it and save again.`;
		case 'element_set_not_attached':
			return `${setName} is not attached to this card.`;
		default:
			return null;
	}
}

export const SET_ERROR_CODES = ['element_set_conflict', 'element_set_redundant', 'element_set_scope', 'element_set_not_found', 'element_set_not_attached'];

// The badge an attached id the cache cannot resolve wears.
const UNRESOLVED_SET = Object.freeze({ name: 'Unavailable element set', icon: '', color: '' });

const scopeTagOf = set => (set?.scope === 'global'
	? <Tag color='blue'>Global</Tag>
	: <Tag color='purple'>This collection</Tag>);

export default function CardElementSets({
	// The ids this card attaches, in draft order — THE list every change is
	// made over. An id the cache cannot resolve (the sets fetch failed, or a
	// set this viewer cannot read) still rows here, unresolved, and rides
	// through every add, move and remove: the card carries it, and a save
	// that touched the list must round-trip it, never silently wipe it.
	attachedIds = null,
	// The set documents this card attaches, in draft order (the resolved
	// half of `attachedIds`; the whole list when no ids are given).
	attached = [],
	// [{ set, card }] — the sets ancestors attach, root-first.
	inherited = [],
	// The sets this card MAY attach (already minus attached and inherited).
	available = [],
	// The whole list, for the refusal's set name.
	setsById = {},
	onChange,
	// async (set) => void; null when the card cannot materialize (unsaved).
	onMaterialize = null,
	// Why "Copy into this card" is off right now, or null when it is on.
	materializeDisabledReason = null,
	// { set, tokens, description } — the primary date a just-attached set
	// suggests, or null (the card editor decides when there is one).
	primaryDateSuggestion = null,
	onUsePrimaryDate = null,
	// The last refused save, when it names a set.
	error = null,
	disabled = false,
}) {
	const navigate = useNavigate();
	const [adding, setAdding] = useState(false);
	// "Not now", per set, for this visit — the draft is not touched.
	const [dismissedOffers, setDismissedOffers] = useState([]);
	const offer = primaryDateSuggestion && !dismissedOffers.includes(normalizeId(primaryDateSuggestion.set?._id))
		? primaryDateSuggestion
		: null;
	const dismissOffer = () => {
		if (!primaryDateSuggestion) return;
		setDismissedOffers(prev => [...prev, normalizeId(primaryDateSuggestion.set?._id)]);
	};
	// [{ id, set }] in draft order, `set` null when the id does not resolve.
	const rows = useMemo(() => {
		if (!Array.isArray(attachedIds)) return attached.map(set => ({ id: normalizeId(set?._id), set }));
		const byId = new Map(attached.map(set => [normalizeId(set?._id), set]));
		return attachedIds.map(normalizeId).filter(Boolean).map(id => ({ id, set: byId.get(id) || setsById[id] || null }));
	}, [attachedIds, attached, setsById]);
	const ids = useMemo(() => rows.map(row => row.id), [rows]);

	const move = (index, delta) => {
		const target = index + delta;
		if (target < 0 || target >= ids.length) return;
		const next = [...ids];
		[next[index], next[target]] = [next[target], next[index]];
		onChange(next);
	};

	const remove = (set, id = normalizeId(set?._id)) => {
		confirmDestructive(Modal, {
			title: `Remove ${set?.name || 'this element set'} from this card?`,
			content: 'Its fields leave the form. Values already stored on items are not deleted: they stay on the items, hidden, and attaching the set again brings them back. When you save, keepr tells you how many items hold one. A manager can purge them from the card\'s Danger zone.',
			okText: 'Remove',
			onOk: () => onChange(ids.filter(other => other !== id)),
		});
	};

	const materialize = (set) => {
		if (!onMaterialize) return;
		const count = (set?.elements || []).length;
		Modal.confirm({
			title: `Copy ${set?.name || 'this element set'} into this card?`,
			content: `Its ${formatCount(count, 'element')} become this card's own elements, with the same names, and the set is detached. Every stored value stays exactly where it is. Later changes to the set will not reach this card.`,
			okText: 'Copy into this card',
			cancelText: 'Cancel',
			onOk: () => onMaterialize(set),
		});
	};

	const rowMenu = (set, index, id) => buildActionMenu([
		// An unresolved set has no page to open and nothing to copy; it can
		// still be moved and removed.
		set ? { key: 'open', group: 'navigate', glyph: 'open', label: 'Open set', onClick: () => navigate(elementSetPath(set)) } : null,
		{ key: 'up', group: 'record', icon: <CHEVRON_UP />, label: 'Move up', disabled: index === 0, onClick: () => move(index, -1) },
		{ key: 'down', group: 'record', icon: <CHEVRON_DOWN />, label: 'Move down', disabled: index === ids.length - 1, onClick: () => move(index, 1) },
		set ? {
			key: 'materialize',
			group: 'record',
			glyph: 'duplicate',
			label: 'Copy into this card',
			disabled: !onMaterialize || !!materializeDisabledReason,
			title: materializeDisabledReason || undefined,
			onClick: () => materialize(set),
		} : null,
		{ key: 'remove', group: 'destructive', glyph: 'remove', label: 'Remove', onClick: () => remove(set, id) },
	]);

	// Global sets first, then the collection's — the order the API lists them.
	const options = useMemo(() => {
		const global = available.filter(set => set?.scope === 'global');
		const own = available.filter(set => set?.scope !== 'global');
		const toOption = set => ({ value: normalizeId(set._id), label: set.name || '(untitled set)', searchText: `${set.name || ''} ${set.description || ''}` });
		const groups = [];
		if (global.length) groups.push({ label: 'Global', options: global.map(toOption) });
		if (own.length) groups.push({ label: 'This collection', options: own.map(toOption) });
		return groups;
	}, [available]);

	const refusal = describeSetRefusal(error, { setsById });

	return (
		<div className='card-element-sets' data-testid='card-element-sets'>
			<GroupHeader label='Element sets' count={rows.length + inherited.length} countLabel='set' as='h4' />
			<p className='form-panel__hint'>
				A reusable set of elements this card attaches. Its fields land on every item of this card, in a panel named for the set, and every card that attaches it shares the definition.
			</p>

			{refusal ? (
				<Alert className='card-element-sets__error' type='error' showIcon message={refusal} />
			) : null}

			{(rows.length || inherited.length) ? (
				<ul className='card-element-sets__list'>
					{rows.map(({ id, set }, index) => (
						<li key={id} className={set ? 'card-element-sets__row' : 'card-element-sets__row card-element-sets__row--unresolved'} data-testid='card-element-set-row'>
							{set ? <>
								<RecordBadge kind='elementSet' record={set} title={set.name || '(untitled set)'} size='sm' to={elementSetPath(set)} />
								<span className='card-element-sets__facts'>{formatCount((set.elements || []).length, 'element')}</span>
								{scopeTagOf(set)}
							</> : <>
								<RecordBadge kind='elementSet' record={UNRESOLVED_SET} title={UNRESOLVED_SET.name} size='sm' />
								<span className='card-element-sets__facts'>could not be loaded, still attached</span>
							</>}
							<span className='card-element-sets__actions'>
								<Dropdown trigger={['click']} placement='bottomRight' menu={rowMenu(set, index, id)} disabled={disabled}>
									<Button type='text' size='small' icon={<KEBAB />} aria-label={`${set?.name || 'Unavailable element set'} actions`} />
								</Dropdown>
							</span>
						</li>
					))}
					{inherited.map(({ set, card }) => (
						<li key={`inherited-${normalizeId(set._id)}`} className='card-element-sets__row card-element-sets__row--inherited' data-testid='card-element-set-row'>
							<RecordBadge kind='elementSet' record={set} title={set.name || '(untitled set)'} size='sm' to={elementSetPath(set)} />
							<span className='card-element-sets__facts'>{formatCount((set.elements || []).length, 'element')}</span>
							<span className='card-element-sets__facts'>{`from ${card?.name || 'a parent card'}`}</span>
						</li>
					))}
				</ul>
			) : (
				<p className='card-element-sets__empty empty-line'>No element sets attached</p>
			)}

			{offer ? (
				<Alert
					className='card-element-sets__offer'
					type='info'
					showIcon
					closable
					onClose={dismissOffer}
					data-testid='primary-date-offer'
					message={`Use ${offer.description} as this card's primary date?`}
					description={`${offer.set?.name || 'The set'} suggests it. Items would sort and lead with the first of these that has a value. Nothing changes until you save.`}
					action={(
						<span className='card-element-sets__offer-actions'>
							<Button size='small' type='primary' disabled={disabled} onClick={() => { if (onUsePrimaryDate) onUsePrimaryDate(offer.tokens); }}>Use it</Button>
							<Button size='small' onClick={dismissOffer}>Not now</Button>
						</span>
					)}
				/>
			) : null}

			{adding ? (
				<Select
					className='card-element-sets__add'
					aria-label='Add element set'
					placeholder='Choose an element set'
					autoFocus
					open
					showSearch
					optionFilterProp='searchText'
					options={options}
					value={undefined}
					onChange={(value) => {
						setAdding(false);
						if (value) onChange([...ids, value]);
					}}
					onBlur={() => setAdding(false)}
					notFoundContent='Every element set you can use is already attached.'
					popupMatchSelectWidth={false}
				/>
			) : (
				<Button icon={<PLUS />} onClick={() => setAdding(true)} disabled={disabled || !available.length} title={available.length ? undefined : 'Every element set you can use is already attached'}>
					Add element set…
				</Button>
			)}
		</div>
	);
}
